/**
 * CartContext
 *
 * Global state management for the shopping cart feature.
 * Allows users to add multiple classes and checkout in a single batch.
 * Persists cart to localStorage for UX continuity across page reloads.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { ClassData } from "../types";

const CART_STORAGE_KEY = "yoga-cart-items";

interface CartContextType {
  /** Array of classes currently in the cart */
  cartItems: ClassData[];
  /** Number of items in the cart */
  cartCount: number;
  /** Total cost of all items in the cart */
  cartTotal: number;
  /** Add a class to the cart. No-op if class is already in cart. */
  addToCart: (classItem: ClassData) => void;
  /** Remove a class from the cart by its ID */
  removeFromCart: (classId: string) => void;
  /** Clear all items from the cart */
  clearCart: () => void;
  /** Check if a class is already in the cart */
  isInCart: (classId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

/**
 * CartProvider wraps the app to provide global cart state.
 * Cart data is persisted to localStorage.
 */
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<ClassData[]>(() => {
    // Initialize from localStorage
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
      return [];
    }
  });

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }, [cartItems]);

  const cartCount = cartItems.length;

  const cartTotal = cartItems.reduce((total, item) => {
    const cost = typeof item.cost === "number" ? item.cost : 0;
    return total + cost;
  }, 0);

  /**
   * Add a class to the cart.
   * Prevents duplicates: if the class ID already exists, this is a no-op.
   */
  const addToCart = useCallback((classItem: ClassData) => {
    setCartItems((prevItems) => {
      // Prevent duplicates - check if class is already in cart
      const alreadyInCart = prevItems.some((item) => item.id === classItem.id);
      if (alreadyInCart) {
        return prevItems;
      }
      return [...prevItems, classItem];
    });
  }, []);

  /**
   * Remove a class from the cart by its ID.
   */
  const removeFromCart = useCallback((classId: string) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== classId)
    );
  }, []);

  /**
   * Clear all items from the cart.
   */
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  /**
   * Check if a class is currently in the cart.
   */
  const isInCart = useCallback(
    (classId: string): boolean => {
      return cartItems.some((item) => item.id === classId);
    },
    [cartItems]
  );

  const value: CartContextType = {
    cartItems,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

/**
 * Hook to access the cart context.
 * Must be used within a CartProvider.
 */
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
